/**
 * Sentry integration for error tracking in web application
 */

import * as Sentry from '@sentry/react'
import {
  browserTracingIntegration,
  captureConsoleIntegration,
  replayIntegration,
} from '@sentry/react'

import { getAppDist, getAppEnvironment, getAppRelease } from './appEnvironment'
import {
  filterSentryEvent,
  sentryDenyUrls,
  sentryIgnoreErrors,
} from './sentryFilters'

let isInitialized = false

export function initSentry() {
  if (isInitialized) {
    return
  }

  // DSN only from build env — no hardcoded fallback (staging/prod must use separate Sentry projects).
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()

  if (!dsn) {
    const environment = getAppEnvironment()
    if (environment === 'staging' || environment === 'production') {
      console.warn('[SENTRY] DSN not configured for deployed environment — skipping initialization')
    } else {
      console.warn('[SENTRY] DSN not configured, skipping initialization')
    }
    return
  }

  const environment = getAppEnvironment()
  const release = getAppRelease()
  const dist = getAppDist()
  const isDeployedEnv = environment === 'staging' || environment === 'production'

  try {
    Sentry.init({
      dsn,
      // Same-origin tunnel so ad blockers that block ingest.de.sentry.io do not drop events.
      tunnel: '/api/sentry-tunnel',
      environment,
      ...(release ? { release } : {}),
      ...(dist ? { dist } : {}),
      // Setting this option to true will send default PII data to Sentry
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      // Structured logs via Sentry.logger — not console.log flood
      enableLogs: true,
      denyUrls: sentryDenyUrls,
      ignoreErrors: sentryIgnoreErrors,
      integrations: [
        browserTracingIntegration({
          // Set tracing origins to track performance
          tracePropagationTargets: ['localhost', /^https:\/\/.*\.supabase\.co/],
        }),
        replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
        // console.error from app code (not devLog — that uses console.log in DEV only)
        ...(isDeployedEnv
          ? [
              captureConsoleIntegration({
                levels: ['error'],
              }),
            ]
          : []),
      ],
      // Performance Monitoring
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
      // Session Replay
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      beforeSend: filterSentryEvent,
    })

    isInitialized = true
    console.log('[SENTRY] ✅ Initialized successfully')
  } catch (error) {
    console.error('[SENTRY] ❌ Failed to initialize:', error)
  }
}

export function setUser(userId: string, email?: string) {
  if (!isInitialized) return

  Sentry.setUser({
    id: userId,
    email,
  })
}

// Export logger for structured logging
// Create a proxy that forwards to Sentry.logger when initialized
const noOpLogger = {
  trace: (_message: string, _data?: Record<string, unknown>) => {},
  debug: (_message: string, _data?: Record<string, unknown>) => {},
  info: (_message: string, _data?: Record<string, unknown>) => {},
  warn: (_message: string, _data?: Record<string, unknown>) => {},
  error: (_message: string, _data?: Record<string, unknown>) => {},
  fatal: (_message: string, _data?: Record<string, unknown>) => {},
  fmt: (strings: TemplateStringsArray, ...values: unknown[]) => {
    // Simple template literal implementation for no-op
    return strings.reduce((result, str, i) => result + str + (values[i] ?? ''), '')
  },
}

export const logger = new Proxy(noOpLogger, {
  get(target, prop) {
    if (isInitialized && Sentry.logger && prop in Sentry.logger) {
      return (Sentry.logger as Record<string, unknown>)[prop]
    }
    return (target as Record<string, unknown>)[prop]
  },
}) as {
  trace: (message: string, data?: Record<string, unknown>) => void
  debug: (message: string, data?: Record<string, unknown>) => void
  info: (message: string, data?: Record<string, unknown>) => void
  warn: (message: string, data?: Record<string, unknown>) => void
  error: (message: string, data?: Record<string, unknown>) => void
  fatal: (message: string, data?: Record<string, unknown>) => void
  fmt: (strings: TemplateStringsArray, ...values: unknown[]) => string
}

// Helper function for startSpan with proper typing
export function startSpan<T>(
  options: { op: string; name: string },
  callback: (span: { setAttribute?: (key: string, value: string | number | boolean) => void } | null) => T | Promise<T>
): Promise<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (Sentry.startSpan as any)(options, callback)
}

export { Sentry }
